import { AddCircle, Inventory } from "@mui/icons-material";
import { Container, Grid, CircularProgress, useTheme } from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import { productStore } from "../../../../app/stores/Product";
import { BasicButton } from "../../../../components/BasicButton";
import { PortalScreenHeader } from "../../../../components/PortalScreenHeader";
import { AddProductForm } from "./components/AddProductForm";
import { ProductsTable } from "./components/ProductsTable";
import { DummyProductsBanner } from "./components/DummyProductsBanner";
import PortalService from "../../../../services/supabase/PortalService";
import { PermissionWrapper } from "../../../../components/PermissionWrapper";
import { Product } from "../../../../models/DB";
import { ProductEditorView } from "./components/ProductEditorView";

const Products = () => {
    const theme = useTheme();
    const portalService = PortalService();
    const [openAddProductForm, setOpenAddProductForm] = useState(false);
    const [editingProduct, setEditingProduct] = useState<Product | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const { allProducts } = productStore(state => state);

    useEffect(() => {
        const fetchProducts = async () => {
            setIsLoading(true);
            await portalService.getAllProducts();
            setIsLoading(false);
        };
        fetchProducts();
    }, []);

    const handleOpenAddProductForm = () => {
        setOpenAddProductForm(true);
    };

    const handleCloseAddProductForm = () => {
        setOpenAddProductForm(false);
    };

    const handleEditProduct = (product: Product) => {
        setEditingProduct(product);
    };

    const handleCloseEditor = () => {
        setEditingProduct(null);
    };

    const handleDummyProductsDeleted = async () => {
        await portalService.getAllProducts();
    };

    // Check if there are any dummy products
    const hasDummyProducts = useMemo(() => {
        return allProducts.some((product: any) => product.isDummyProduct === true);
    }, [allProducts]);

    // If editing a product, show the editor view
    if (editingProduct) {
        return (
            <PermissionWrapper screen="products" permission="update">
                <Container
                    component="main"
                    maxWidth="xl"
                    sx={{
                        height: "100%",
                        paddingX: { xs: 0, md: 2 },
                    }}>
                    <ProductEditorView
                        product={editingProduct}
                        onClose={handleCloseEditor}
                    />
                </Container>
            </PermissionWrapper>
        );
    }

    return (
        <PermissionWrapper screen="products" permission="view">
            <Container
                component="main"
                maxWidth="xl"
                sx={{
                    height: "100%",
                    paddingX: { xs: 0, md: 2 },
                }}>
                {
                    isLoading ?
                        <Grid container justifyContent="center" mt={20}>
                            <Grid item>
                                <CircularProgress
                                    size={40}
                                    sx={{ color: theme.palette.primary.main }}
                                />
                            </Grid>
                        </Grid>
                        :
                        <>
                            <PortalScreenHeader
                                title="Products"
                                subtitle="Manage your product catalog and inventory"
                                icon={<Inventory />}
                                mobileActionIcon={<AddCircle />}
                                onMobileActionClick={handleOpenAddProductForm}
                                actions={
                                    <PermissionWrapper screen="products" permission="create">
                                        <BasicButton
                                            title="Add Product"
                                            onClick={handleOpenAddProductForm}
                                            startIcon={<AddCircle />}
                                        />
                                    </PermissionWrapper>
                                }
                            />
                            <PermissionWrapper screen="products" permission="delete">
                                <DummyProductsBanner
                                    hasDummyProducts={hasDummyProducts}
                                    onDeleted={handleDummyProductsDeleted}
                                />
                            </PermissionWrapper>
                            <ProductsTable
                                products={allProducts}
                                onEditProduct={handleEditProduct}
                            />
                            {
                                openAddProductForm &&
                                <AddProductForm
                                    products={allProducts}
                                    onClose={handleCloseAddProductForm}
                                />
                            }
                        </>
                }
            </Container>
        </PermissionWrapper>
    );
};

export default Products;
